;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { fetchIdempotentCleanup, fetchWithIdempotency } from '@workspace/request-idempotency';
// Removed circular import - these functions are defined in this file

import { resetPostHogUser } from '@workspace/analytics/posthog/client';
import { resetSentryUser } from '@workspace/analytics/sentry/user';
import {
  ACCESSIBLE_APPS,
  AUTH_SESSION_SYNC_HEADER,
  AUTH_SESSION_SYNC_HEADER_VALUE,
  coordinateAuthTokenRefresh,
  getAuthRefreshMarker,
  waitForAuthRefreshMarkerChange,
} from '@workspace/ui/lib';
import type { JwtPayload } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import { createJwtUserFromPayload } from '@/lib/active-organisation';
import { useAuthStore } from '@/stores/authStore';

let sessionSyncPromise: Promise<boolean> | null = null;

export const login = async (email: string, password: string) => {
  const res = await fetchWithIdempotency('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Login failed' }));

    // Handle 2FA case
    if (res.status === 428) {
      return { requires2FA: true, message: errorData.message };
    }

    throw new Error(errorData.error || 'Login failed');
  }

  const data = await res.json();

  if (!data.access_token) {
    throw new Error('Invalid response from server');
  }

  const decodedToken = jwtDecode<JwtPayload>(data.access_token);

  // Allow EMPLOYER, EMPLOYEE and CONTRACTOR users to access wallet too (for now)
  if (
    decodedToken.accessible_app !== ACCESSIBLE_APPS.WALLET &&
    decodedToken.accessible_app !== ACCESSIBLE_APPS.EMPLOYER &&
    decodedToken.accessible_app !== ACCESSIBLE_APPS.EMPLOYEE &&
    decodedToken.accessible_app !== ACCESSIBLE_APPS.CONTRACTOR &&
    decodedToken.accessible_app !== ACCESSIBLE_APPS.GIG_WORKER &&
    decodedToken.accessible_app !== ACCESSIBLE_APPS.INDIVIDUAL_CRAFT
  ) {
    await logout();

    const error = new Error('Access denied: Invalid role for this application') as Error & {
      statusCode: number;
    };

    error.statusCode = 403;
    throw error;
  }

  const user = createJwtUserFromPayload(decodedToken);

  useAuthStore.getState().setAccessToken(data.access_token);
  useAuthStore.getState().setAuthenticated(true);
  useAuthStore.getState().setUser(user);

  return data;
};

export const logout = async () => {
  try {
    const res = await fetchWithIdempotency('/api/auth/logout', { method: 'POST' });
    const data = await res.json();

    // Even if the API call fails, we still want to clear the local state
    resetPostHogUser();
    resetSentryUser();
    useAuthStore.getState().setAccessToken(null);
    useAuthStore.getState().setAuthenticated(false);
    useAuthStore.getState().setUser(null);

    if (!res.ok) {
      console.warn('Logout API call failed:', data.error || 'Logout failed');
    }
  } catch (error) {
    // If the API call fails completely, still clear the local state
    resetPostHogUser();
    resetSentryUser();
    useAuthStore.getState().setAccessToken(null);
    useAuthStore.getState().setAuthenticated(false);
    useAuthStore.getState().setUser(null);
    console.warn('Logout failed:', error);
  }
};

export function syncAuthStateFromAccessToken(accessToken: string): void {
  if (useAuthStore.getState().accessToken === accessToken) return;

  const decodedToken = jwtDecode<JwtPayload>(accessToken);

  const user = createJwtUserFromPayload(decodedToken);

  useAuthStore.getState().setAccessToken(accessToken);
  useAuthStore.getState().setAuthenticated(true);
  useAuthStore.getState().setUser(user);
}

export function syncAuthSessionFromCookies(): Promise<boolean> {
  if (sessionSyncPromise) return sessionSyncPromise;

  sessionSyncPromise = (async () => {
    try {
      const response = await fetchWithIdempotency('/api/auth/session-sync', {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          [AUTH_SESSION_SYNC_HEADER]: AUTH_SESSION_SYNC_HEADER_VALUE,
        },
        cache: 'no-store',
      });

      if (response.status === 401) {
        useAuthStore.getState().setAccessToken(null);
        useAuthStore.getState().setAuthenticated(false);
        useAuthStore.getState().setUser(null);
        return true;
      }

      if (!response.ok) return false;

      const data = await response.json();
      if (!data.access_token) return false;

      syncAuthStateFromAccessToken(data.access_token);
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    sessionSyncPromise = null;
  });

  return sessionSyncPromise;
}

export const refreshToken = async () =>
  coordinateAuthTokenRefresh(async () => {
    try {
      const previousRefreshMarker = getAuthRefreshMarker();
      const res = await fetchWithIdempotency('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      const data = await res.json();
      if (res.status === 409) {
        const hasCompletedRefresh = await waitForAuthRefreshMarkerChange(previousRefreshMarker);
        if (!hasCompletedRefresh) return { success: false, isProcessing: true };

        const hasSynchronized = await syncAuthSessionFromCookies();
        const synchronizedAccessToken = useAuthStore.getState().accessToken;
        if (!hasSynchronized) return { success: false, isProcessing: true };
        if (!synchronizedAccessToken) return { success: false };

        return {
          success: true,
          data: { access_token: synchronizedAccessToken },
        };
      }
      if (!res.ok || !data.access_token) return { success: false };

      const decodedToken = jwtDecode<JwtPayload>(data.access_token);

      // Allow EMPLOYER, EMPLOYEE and CONTRACTOR users to access wallet too (for now)
      if (
        decodedToken.accessible_app !== ACCESSIBLE_APPS.WALLET &&
        decodedToken.accessible_app !== ACCESSIBLE_APPS.EMPLOYER &&
        decodedToken.accessible_app !== ACCESSIBLE_APPS.EMPLOYEE &&
        decodedToken.accessible_app !== ACCESSIBLE_APPS.CONTRACTOR &&
        decodedToken.accessible_app !== ACCESSIBLE_APPS.GIG_WORKER &&
        decodedToken.accessible_app !== ACCESSIBLE_APPS.INDIVIDUAL_CRAFT
      ) {
        await logout();

        const error = new Error('Access denied: Invalid role for this application') as Error & {
          statusCode: number;
        };
        error.statusCode = 403;
        throw error;
      }

      return {
        success: true,
        data: { access_token: data.access_token },
      };
    } catch {
      return { success: false };
    }
  }, syncAuthStateFromAccessToken);

export const forgotPassword = async (email: string) => {
  const response = await fetchWithIdempotency(
    `${process.env.NEXT_PUBLIC_API_URL}/forgot-password`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }
  );
  const data = (await response.json()) as { message?: string };

  return {
    success: response.ok,
    message: data.message,
  };
};

export const resetPassword = async (
  email: string,
  token: string,
  password: string,
  password_confirmation: string
) => {
  const response = await fetchWithIdempotency(`${process.env.NEXT_PUBLIC_API_URL}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      token,
      password,
      password_confirmation,
    }),
  });
  const data = (await response.json()) as { message?: string };

  return {
    success: response.ok,
    message: data.message,
  };
};

export const clear2FA = async () => {
  try {
    const res = await fetchWithIdempotency('/api/auth/2fa/clear', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error('Failed to clear 2FA state');
    }

    return { success: true };
  } catch (error) {
    console.error('Error clearing 2FA:', error);
    throw error;
  }
};

export const verify2FA = async (code: string) => {
  const res = await fetchWithIdempotency('/api/auth/2fa/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: '2FA verification failed' }));
    throw new Error(errorData.error || '2FA verification failed');
  }

  const data = await res.json();

  if (!data.access_token) {
    throw new Error('Invalid response from server');
  }

  const decodedToken = jwtDecode<JwtPayload>(data.access_token);

  const user = createJwtUserFromPayload(decodedToken);

  useAuthStore.getState().setAccessToken(data.access_token);
  useAuthStore.getState().setAuthenticated(true);
  useAuthStore.getState().setUser(user);

  return data;
};

export const clearAuthCookies = async () => {
  resetPostHogUser();
  resetSentryUser();

  const didClear = await fetchIdempotentCleanup('/api/auth/clear-auth-cookies', {
    method: 'POST',
  });

  if (!didClear) {
    console.warn('Failed to clear auth cookies');
    return { success: false };
  }

  return { success: true };
};
